/**
 * @author        woody@zumper.com (Woody Anderson)
 * @copyright     Copyright (c) 2017, Zumper
 * @description   Configure the redux store for the NZR app
 */

import { configureStore } from '@reduxjs/toolkit'
import { selectNow, updateNow } from '@zcode-ui/web-zumper-global-state'
import { createTraktorMiddleware } from '@zcode-ui/web-zumper-integrations'
import { reviveBizos } from '@zcode/bizos'
import { IS_NOT_PRODUCTION, ON_BROWSER, ON_SERVER } from '@zcode/nr/es/shared/env'
import { createHistoryMiddleware } from '@zcode/nr/es/shared/packages/history-middleware'
import { createOfflineMiddleware } from '@zcode/nr/es/shared/packages/offline-middleware'
import { createOptimizelyMiddleware } from '@zcode/nr/es/shared/packages/optimizely-middleware'
import { batch } from 'react-redux'
import { batchedSubscribe } from 'redux-batched-subscribe'
import thunk from 'redux-thunk'
import { nzrOnlyTraktorMiddleware } from '../modules/traktor/middleware/index'
import { rootReducer as appReducer } from '../reducers/index'

let pending = null
const batchedNotify = (notify) => {
  if (pending) {
    clearTimeout(pending)
  }
  // effectively debounce notify, dropping notify calls
  // uses batch to ensure notifications are also delayed until react is ready
  pending = setTimeout(() => {
    pending = null
    batch(notify)
  }, 0)
}

/**
 *
 * @param {any} state
 * @param {any} param2
 * @returns {import('../modules/types').Store}
 */
const factory = (state, { getGeoLocation, history, optimizely }) => {
  const thunkExtraArg = { getGeoLocation, history, optimizely }
  const middleware = [
    thunk.withExtraArgument(thunkExtraArg),
    createTraktorMiddleware(nzrOnlyTraktorMiddleware),
    history && createHistoryMiddleware(history),
    optimizely && createOptimizelyMiddleware(optimizely),
    createOfflineMiddleware(),
  ].filter(Boolean)
  reviveBizos(state)
  const store = configureStore({
    reducer: appReducer,
    middleware,
    preloadedState: state,
    enhancers: !ON_SERVER && [batchedSubscribe(batchedNotify)],
    devTools: IS_NOT_PRODUCTION && ON_BROWSER,
  })
  thunkExtraArg.subscribe = store.subscribe
  store.getGeoLocation = getGeoLocation
  if (ON_SERVER && Math.abs(Date.now() - selectNow(store.getState())) > 10) {
    // @zumper/redux-ignore annoyingly caches the "initial value" of reducers, so that
    // state.app.now (which should be set to Date.now()) each time we create a new redux
    // store) ends up being "stuck" with a value that was generated when the server started.
    // This bit of code updates state.app.now when that happens
    store.dispatch(updateNow())
  }
  return store
}

export default factory
